/**
 * The Quick Access Toolbar — Pinned screens as icon buttons in the host's top
 * bar (published through `setTopBarSlots`), and "Customize quick access",
 * which opens the Menu panel's Pinned section (reorder / unpin).
 *
 * A WAI-ARIA toolbar: one tab stop, Left / Right / Home / End move between
 * its buttons. Every button is icon-only, so each carries an aria-label and a
 * tooltip that shows on hover AND focus.
 */
import { ChevronDown } from 'lucide-react'
import { useRef, useState, type KeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import { S, iconButton } from '../../nav/tokens'
import type { RunNavItem } from '../../nav/types'
import type { ShellEntry } from '../../nav/useShellNav'
import { IconTip, moveFocus } from './shared'
import { useDragReorder } from '../../nav/parts/useDragReorder'

export function QuickAccess({ pinned, currentId, onOpen, onCustomize, customizeOpen, onMove }: {
  pinned: ShellEntry[]
  currentId: string | null
  onOpen: (item: RunNavItem) => void
  onCustomize: (from: HTMLElement) => void
  customizeOpen: boolean
  /** Drag a button sideways to reorder Pinned (live preview of the drop). */
  onMove?: (item: RunNavItem, delta: number) => void
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const { bind } = useDragReorder({
    axis: 'x', containerRef: barRef, disabled: !onMove || pinned.length < 2,
    onMove: (key, to) => {
      const from = pinned.findIndex((e) => e.item.id === key)
      if (from >= 0 && from !== to) onMove?.(pinned[from].item, to - from)
    },
  })
  // Roving tab stop: the last button focused, else the first.
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const keys = [...pinned.map((e) => e.item.id), '__customize']
  const stop = focusKey && keys.includes(focusKey) ? focusKey : keys[0]
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-qat]')]
    if (moveFocus(els, e.key)) e.preventDefault()
  }
  return (
    <div ref={barRef} role="toolbar" aria-label="Quick access: pinned screens" onKeyDown={onKey}
      {...tw('action', 'shell-office-qat-bar')}
      style={{
        display: 'flex', alignItems: 'center', gap: 2, marginLeft: 4, paddingLeft: 8,
        borderLeft: `1px solid ${S.border}`, minWidth: 0,
      }}>
      {pinned.map((e) => {
        const on = e.item.id === currentId
        return (
          <span key={e.item.id} {...bind(e.item.id)} style={{ display: 'inline-flex', position: 'relative' }}>
          <IconTip label={e.pathText ? `${e.item.title} — ${e.pathText}` : e.item.title}>
            <button type="button" data-qat="" className="tw-sh-btn"
              tabIndex={stop === e.item.id ? 0 : -1}
              onFocus={() => setFocusKey(e.item.id)}
              onClick={() => onOpen(e.item)}
              aria-label={`Open ${e.item.title}`}
              aria-current={on ? 'page' : undefined}
              {...tw('action', 'shell-office-qat', { obj: e.item.obj_name, type: e.item.obj_type, selected: on })}
              style={{
                ...iconButton(34),
                color: on ? S.accent : S.text2,
                background: on ? S.accentWeak : 'transparent',
              }}>
              <NavIcon node={e.item} size={17} />
            </button>
          </IconTip>
          </span>
        )
      })}
      <IconTip label="Customize quick access">
        <button type="button" data-qat="" className="tw-sh-btn"
          tabIndex={stop === '__customize' ? 0 : -1}
          onFocus={() => setFocusKey('__customize')}
          onClick={(ev) => onCustomize(ev.currentTarget)}
          aria-label="Customize quick access"
          aria-haspopup="dialog"
          aria-expanded={customizeOpen}
          {...tw('action', 'shell-office-qat-customize')}
          style={{ ...iconButton(34), width: 26 }}>
          <ChevronDown size={14} strokeWidth={2.4} aria-hidden />
        </button>
      </IconTip>
    </div>
  )
}
